/**
 * Permission registry — the single source of truth for every module, menu,
 * screen and action in the system. The API syncs this into the database on
 * startup and the web app uses it to render navigation and the role matrix.
 *
 * Key formats:
 *   module access : `<module>.access`
 *   menu access   : `<module>.menu.<menu>`
 *   screen action : `<module>.<screen>.<action>`
 */

export const STANDARD_ACTIONS = [
  'view',
  'create',
  'edit',
  'delete',
  'approve',
  'reject',
  'print',
  'export',
  'post',
  'unpost',
  'cancel',
  'reopen',
] as const;
export type StandardAction = (typeof STANDARD_ACTIONS)[number];

export const ACTION_LABELS: Record<StandardAction, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  reject: 'Reject',
  print: 'Print',
  export: 'Export',
  post: 'Post',
  unpost: 'Unpost',
  cancel: 'Cancel',
  reopen: 'Reopen',
};

/** Data scope: which records an allowed action applies to. Widest wins across roles. */
export const DATA_SCOPES = ['OWN', 'DEPARTMENT', 'BRANCH', 'ALL'] as const;
export type DataScope = (typeof DATA_SCOPES)[number];
export const DATA_SCOPE_LABELS: Record<DataScope, string> = {
  OWN: 'Own records',
  DEPARTMENT: 'Own department',
  BRANCH: 'Own branch(es)',
  ALL: 'All records',
};
export const SCOPE_RANK: Record<DataScope, number> = { OWN: 0, DEPARTMENT: 1, BRANCH: 2, ALL: 3 };

export interface SpecialAction {
  key: string;
  label: string;
}

export interface ScreenDef {
  key: string;
  label: string;
  /** Standard actions that make sense for this screen. */
  actions: readonly StandardAction[];
  /** Screen-specific administrative actions. */
  special?: readonly SpecialAction[];
  /** Whether data-scope (own/department/branch/all) applies to this screen. */
  scoped?: boolean;
  /** Permission keys that also grant this screen's `view` (for screens added later, so roles keep working). */
  viewImpliedBy?: readonly string[];
  /** Shorter name for the sidebar when the label does not fit (the page keeps the full label). */
  navLabel?: string;
  /** Not listed in the sidebar: reached as a tab of a hub screen (permissions still apply). */
  hidden?: boolean;
  /** Hub screen whose tab shows this screen (highlighted in the sidebar while it is open). */
  hub?: string;
  /** A hub that also opens for whoever may view one of its tabs, showing only those tabs (owner, 2026-10-30). */
  openedByTabs?: boolean;
}

export interface MenuDef {
  key: string;
  label: string;
  screens: readonly ScreenDef[];
  /**
   * Older menu keys this menu took over. On startup every role holding one of them is
   * granted this menu too, so regrouping a sidebar never takes access away.
   */
  replaces?: readonly string[];
}

export interface ModuleDef {
  key: ModuleKey;
  name: string;
  shortName: string;
  description: string;
  /** Display order on the module-selection screen. */
  order: number;
  menus: readonly MenuDef[];
  /** Sidebar shows an Overview heading and the section name in the breadcrumb. */
  sectionNav?: boolean;
}

export const MODULE_KEYS = [
  'sales',
  'inventory',
  'manufacturing',
  'mattress',
  'invoicing',
  'installation',
  'service',
  'accounting',
  'hr',
  'admin',
] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

const CRUD = ['view', 'create', 'edit', 'delete', 'print', 'export'] as const;
const MASTER = ['view', 'create', 'edit', 'delete', 'export'] as const;
const DOC = STANDARD_ACTIONS;
const REPORT = ['view', 'print', 'export'] as const;
/** Extra rights on every workflow document screen. */
/** Partner and item masters can merge duplicate records. */
const MERGE: readonly SpecialAction[] = [{ key: 'merge', label: 'Merge duplicates' }];
const DOC_SPECIAL: readonly SpecialAction[] = [
  { key: 'approve_own', label: 'Approve own documents' },
  { key: 'audit', label: 'Audit check' },
];

export const MODULES: readonly ModuleDef[] = [
  {
    key: 'sales',
    name: 'Order Booking',
    shortName: 'Sales',
    description: 'Customers, quotations and sales orders',
    order: 1,
    sectionNav: true,
    menus: [
      {
        key: 'presales',
        label: 'Enquiries & Design',
        screens: [
          { key: 'inquiry_studio', label: 'Inquiry Studio', actions: ['view'], scoped: true, viewImpliedBy: ['sales.enquiries.view', 'sales.design_board.view'] },
          {
            key: 'enquiries',
            label: 'Enquiries',
            actions: DOC,
            scoped: true,
            special: [
              { key: 'design', label: 'Make drawings and revisions' },
              { key: 'feasibility', label: 'Feasibility review (production)' },
              { key: 'cost', label: 'Cost sheets and prices' },
              { key: 'release', label: 'Release orders to production (and override the advance)' },
              { key: 'see_prices', label: 'See prices and costs (cost, selling price, margin) — the drawings and production teams do not' },
            ],
          },
          { key: 'release_gate', label: 'Release to Production', actions: ['view'], scoped: true, viewImpliedBy: ['sales.enquiries.release'] },
        ],
      },
      {
        key: 'transactions',
        label: 'Selling',
        screens: [
          { key: 'quotations', label: 'Quotations', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'change_vat', label: 'Change VAT rate (other than standard)' }] },
          { key: 'sales_orders', label: 'Sales Orders', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'change_vat', label: 'Change VAT rate (other than standard)' }, { key: 'send_production', label: 'Send to production' }, { key: 'close', label: 'Close remaining quantity' }, { key: 'edit_approved', label: 'Edit approved orders (until dispatch)' }, { key: 'set_rates', label: 'Set pending rates' }] },
        ],
      },
      {
        key: 'catalogue',
        label: 'Catalogue',
        screens: [{ key: 'catalogue', label: 'Catalogue', actions: MASTER }],
      },
      {
        key: 'customers',
        label: 'Customers',
        replaces: ['masters', 'reports'],
        screens: [
          { key: 'customers', label: 'Customers', actions: MASTER, special: MERGE },
          { key: 'customer_360', label: 'Customer 360', actions: ['view', 'export'], scoped: true },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [
          { key: 'sales_reports', label: 'Sales Reports', actions: REPORT, scoped: true },
        ],
      },
      {
        key: 'settings',
        label: 'Configurations',
        replaces: ['masters'],
        screens: [
          { key: 'salespersons', label: 'Salespersons', actions: MASTER },
        ],
      },
    ],
  },
  {
    key: 'inventory',
    name: 'Purchase & Inventory',
    shortName: 'Inventory',
    description: 'Items, stock, purchasing and goods receipts',
    order: 2,
    sectionNav: true,
    menus: [
      // Owner, 2026-11-02: Approvals on its own at the top (with what waits for me); then by who uses the screens.
      {
        key: 'approvals_menu',
        label: 'Approvals',
        screens: [
          { key: 'approvals', label: 'Approvals', actions: ['view'], viewImpliedBy: ['inventory.purchase_requisitions.view', 'inventory.purchase_orders.view', 'inventory.goods_receipts.view', 'inventory.gate_passes.view', 'inventory.stock_issues.view', 'inventory.stock_adjustments.view', 'inventory.purchase_returns.view'] },
        ],
      },
      {
        key: 'purchase',
        label: 'Purchasing',
        replaces: ['catalogue'],
        screens: [
          { key: 'purchase_requisitions', label: 'Requisitions', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'close', label: 'Close the remaining quantity' }, { key: 'override', label: 'Change the demand the system worked out (with a reason)' }] },
          { key: 'purchase_orders', label: 'Purchase Orders', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'close', label: 'Close the remaining quantity' }, { key: 'print_rate_history', label: 'Print the internal copy with rate history (other vendors\' rates)' }] },
          { key: 'suppliers', label: 'Vendors', actions: MASTER, special: MERGE },
          { key: 'reorder', label: 'Reorder', actions: ['view'], viewImpliedBy: ['inventory.purchase_requisitions.create'] },
          // Supplier bills are entered in Accounting; the screen keeps its rights (Accounting › Purchase vouchers).
          { key: 'purchase_bills', hidden: true, label: 'Supplier Bills', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'change_vat', label: 'Change VAT rate' }] },
        ],
      },
      {
        key: 'logistics',
        label: 'Gate & Receiving',
        replaces: ['purchase', 'stock'],
        screens: [
          { key: 'gate_passes', label: 'Inward Gate Passes (IGP)', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'goods_receipts', label: 'Goods Receipts (GRN)', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'inspect', label: 'Record quality check' }, { key: 'over_receive', label: 'Receive more than ordered (within the tolerance)' }, { key: 'override_rate', label: 'Change the bill rate from the PO rate (with the reason)' }] },
          { key: 'out_gate_passes', label: 'Outward Gate Passes (OGP)', actions: DOC, scoped: true, special: DOC_SPECIAL },
        ],
      },
      {
        key: 'stock',
        label: 'Store',
        replaces: ['purchase'],
        screens: [
          { key: 'stock_issues', label: 'Issuance', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'approve_extra', label: 'Approve issue above the plan (extra issue)' }] },
          { key: 'production_returns', label: 'Return from Production', actions: ['view'], viewImpliedBy: ['inventory.stock_issues.view'] },
          { key: 'purchase_returns', label: 'Return to Vendor', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'stock_transfers', hidden: true, label: 'Stock Transfers', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'stock_adjustments', label: 'Stock Adjustments', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'stock_counts', label: 'Stock Counts', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'held_stock', label: 'Stock Held for Orders', actions: ['view'], special: [{ key: 'release', label: 'Release or move held stock' }] },
          { key: 'special_requests', label: 'Special Request Notes', actions: DOC, scoped: true, special: DOC_SPECIAL },
          {
            key: 'stock_valuation',
            label: 'Valuation & Month-end',
            hidden: true,
            hub: 'inventory_reports',
            actions: ['view', 'print', 'export'],
            special: [{ key: 'post_closing', label: 'Post month-end closing stock' }],
          },
        ],
      },
      {
        key: 'catalogue',
        label: 'Master Data',
        replaces: ['masters'],
        screens: [
          { key: 'raw_materials', label: 'Raw Materials', actions: ['view'], viewImpliedBy: ['inventory.items.view'] },
          { key: 'finished_goods', label: 'Finished Goods', actions: ['view'], viewImpliedBy: ['inventory.items.view'] },
          { key: 'items', label: 'All Items', actions: MASTER, special: MERGE },
          { key: 'products', label: 'Products & Categories', actions: MASTER },
          { key: 'departments', label: 'Departments', actions: ['view'], viewImpliedBy: ['admin.departments.view'] },
          { key: 'shelves', label: 'Shelves & Locations', actions: MASTER },
          { key: 'planning_check', label: 'Planning Check', actions: ['view'], viewImpliedBy: ['inventory.items.view'] },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [
          { key: 'order_material', label: 'Order Material Status', actions: ['view', 'print', 'export'], viewImpliedBy: ['inventory.purchase_requisitions.view', 'inventory.stock_issues.view'] },
          { key: 'inventory_reports', label: 'Inventory Reports', actions: REPORT, scoped: true },
          { key: 'store_reports', label: 'Purchasing & Store Reports', actions: REPORT, scoped: true },
        ],
      },
      {
        key: 'settings',
        label: 'Configurations',
        replaces: ['masters'],
        screens: [
          {
            key: 'inventory_settings',
            label: 'Settings',
            actions: ['view'],
            viewImpliedBy: ['inventory.product_attributes.view', 'inventory.item_categories.view', 'inventory.uoms.view', 'inventory.warehouses.view', 'inventory.production_sections.view', 'inventory.purchasing_rules.view', 'inventory.purchasing_cutover.view'],
          },
          { key: 'product_attributes', hidden: true, hub: 'inventory_settings', label: 'Product Attributes', actions: MASTER },
          { key: 'item_categories', hidden: true, hub: 'inventory_settings', label: 'Item Categories', actions: MASTER },
          { key: 'uoms', hidden: true, hub: 'inventory_settings', label: 'Units of Measure', actions: MASTER },
          { key: 'warehouses', hidden: true, hub: 'inventory_settings', label: 'Warehouses', actions: MASTER },
          { key: 'production_sections', hidden: true, hub: 'inventory_settings', label: 'Production Sections', actions: MASTER },
          // Seen by anyone given it; changed only by admins (Administration › System settings: edit).
          { key: 'purchasing_rules', hidden: true, hub: 'inventory_settings', label: 'Purchasing Rules', actions: ['view'] },
          { key: 'purchasing_cutover', hidden: true, hub: 'inventory_settings', label: 'Purchasing Cut-over', actions: ['view'] },
        ],
      },
    ],
  },
  {
    key: 'mattress',
    name: 'Manufacturing - Mattress',
    shortName: 'Mattress',
    description: 'Mattress demand, daily production plan, production orders, materials and output',
    order: 3.5,
    sectionNav: true,
    menus: [
      {
        key: 'command',
        label: 'Command Centre',
        screens: [{ key: 'command_centre', label: 'Command Centre', actions: ['view'] }],
      },
      {
        key: 'planning',
        label: 'Planning',
        screens: [
          { key: 'mattress_demand', label: 'Demand', actions: ['view', 'export'] },
          { key: 'production_plan', label: 'Production Plan', actions: ['view', 'create', 'edit'] },
          { key: 'production_orders', label: 'Production Orders', actions: DOC, special: DOC_SPECIAL },
        ],
      },
      {
        key: 'floor',
        label: 'Shop Floor',
        screens: [
          { key: 'production_entries', label: 'Production Entries', actions: DOC, special: DOC_SPECIAL },
          { key: 'missing_entries', label: 'Missing Entries', actions: ['view'] },
        ],
      },
      {
        key: 'products',
        label: 'Products',
        screens: [{ key: 'mattress_boms', label: 'Bills of Materials', actions: MASTER, special: [{ key: 'approve', label: 'Approve a BOM version' }] }],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [{ key: 'mattress_reports', label: 'Production Reports', actions: REPORT }],
      },
      {
        key: 'settings',
        label: 'Configurations',
        screens: [
          { key: 'mattress_settings', label: 'Settings', actions: ['view'], viewImpliedBy: ['mattress.mattress_stages.view'] },
          { key: 'mattress_stages', hidden: true, hub: 'mattress_settings', label: 'Production Stages', actions: MASTER },
        ],
      },
    ],
  },
  {
    key: 'manufacturing',
    name: 'Manufacturing - Furniture',
    shortName: 'Furniture',
    description: 'Drawings, delivery commitments, planning, materials and furniture production',
    order: 3,
    sectionNav: true,
    menus: [
      {
        key: 'command',
        label: 'Command Centre',
        screens: [{ key: 'command_centre', label: 'Command Centre', actions: ['view'] }],
      },
      {
        key: 'orders',
        label: 'Orders',
        screens: [
          {
            key: 'drawings',
            label: 'Drawings & Commitments',
            actions: ['view', 'edit', 'export'],
            scoped: true,
            special: [
              { key: 'review', label: 'Review drawings' },
              { key: 'commit', label: 'Commit completion / delivery dates' },
            ],
          },
          { key: 'unplanned_orders', label: 'Unplanned Orders', actions: ['view', 'export'], scoped: true },
        ],
      },
      {
        key: 'planning',
        label: 'Planning',
        screens: [
          {
            key: 'planning_studio',
            label: 'Planning Studio',
            actions: ['view'],
            scoped: true,
            viewImpliedBy: ['manufacturing.work_orders.view'],
            special: [
              { key: 'plan', label: 'Plan jobs (make jobs, route & minutes, ask to leave out a main operation)' },
              { key: 'authorise_skip', label: 'Authorise leaving out a main operation for an item' },
              { key: 'mark_outside', label: 'Mark orders produced outside the studio (old system)' },
            ],
          },
          { key: 'work_orders', label: 'Production Jobs', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'material_plans', label: 'Material Plans (jobs)', actions: DOC, scoped: true, special: DOC_SPECIAL, viewImpliedBy: ['manufacturing.work_orders.view'] },
          { key: 'material_requisitions', label: 'Material Requisitions (MRN)', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'issue', label: 'Issue material (and propose substitutes)' }, { key: 'approve_substitute', label: 'Approve substitute material' }, { key: 'close', label: 'Close the remaining quantity' }] },
        ],
      },
      {
        key: 'plan_views',
        label: 'Planning views',
        replaces: ['planning'],
        screens: [
          { key: 'job_board', label: 'Job Board', actions: ['view'], scoped: true },
          {
            key: 'allocation_board',
            label: 'Planning Timeline',
            actions: ['view'],
            scoped: true,
            special: [
              { key: 'replan', label: 'Re-plan (move work on the timeline)' },
              { key: 'override', label: 'Override: frozen window and committed delivery dates' },
              { key: 'approve_plan', label: 'Approve plan changes that move committed dates' },
            ],
          },
          { key: 'calendar_board', label: 'Calendar Board', actions: ['view'], scoped: true, viewImpliedBy: ['manufacturing.allocation_board.view'] },
          { key: 'capacity', label: 'Capacity', actions: ['view'], scoped: true, viewImpliedBy: ['manufacturing.allocation_board.view'] },
        ],
      },
      {
        key: 'shop_floor',
        label: 'Shop Floor',
        screens: [
          { key: 'labour', label: 'Shop Floor', actions: ['view', 'edit'], scoped: true, special: [{ key: 'supervise', label: 'Act for other employees' }] },
          { key: 'job_cards', label: 'Job Card Entry', actions: ['view'], viewImpliedBy: ['manufacturing.labour.supervise'] },
          { key: 'quality_checks', label: 'Quality Check', actions: ['view'], scoped: true, special: [{ key: 'inspect', label: 'Pass a job or send it back for rework' }] },
          { key: 'fg_receipts', label: 'Finished Goods Receipts', actions: ['view', 'print', 'export'], scoped: true, special: [{ key: 'reverse', label: 'Reverse' }] },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [{ key: 'production_reports', label: 'Production Reports', actions: REPORT, scoped: true }],
      },
      {
        key: 'setup',
        label: 'Configurations',
        screens: [
          {
            key: 'production_setup',
            label: 'Production Setup',
            actions: ['view', 'export'],
            viewImpliedBy: ['manufacturing.operation_types.view', 'manufacturing.routes.view'],
            special: [{ key: 'manage', label: 'Manage production setup (operations, categories, planning rules, labour rates)' }],
          },
          { key: 'skills', label: 'Skills Matrix', actions: ['view', 'edit'] },
          { key: 'standard_times', label: 'Standard Times', actions: ['view'], viewImpliedBy: ['manufacturing.production_setup.view'] },
        ],
      },
    ],
  },
  {
    key: 'invoicing',
    name: 'Delivery & Invoicing',
    shortName: 'Invoicing',
    description: 'Deliveries, tax invoices and credit notes',
    order: 4,
    sectionNav: true,
    menus: [
      {
        key: 'transactions',
        label: 'Deliveries & Billing',
        screens: [
          { key: 'delivery_notes', label: 'Delivery Notes', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'tax_invoices', label: 'Tax Invoices', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'credit_notes', label: 'Credit Notes', actions: DOC, scoped: true, special: DOC_SPECIAL },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [{ key: 'invoicing_reports', label: 'Invoicing Reports', actions: REPORT, scoped: true }],
      },
    ],
  },
  {
    key: 'installation',
    name: 'Installation',
    shortName: 'Installation',
    description: 'Fixing at the customer\'s site by our own teams: scheduling, crews, visits, snags and handover',
    order: 4.5,
    sectionNav: true,
    menus: [
      {
        key: 'command',
        label: 'Command Centre',
        screens: [{ key: 'command_centre', label: 'Command Centre', actions: ['view'] }],
      },
      {
        key: 'work',
        label: 'Installations',
        screens: [
          { key: 'installation_orders', label: 'Installation Orders', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'schedule', label: 'Schedule visits and crews' }, { key: 'handover', label: 'Record the customer handover' }] },
          { key: 'team_board', label: 'Team Board', actions: ['view'], scoped: true, viewImpliedBy: ['installation.installation_orders.view'] },
          { key: 'snags', label: 'Snags', actions: ['view', 'edit'], scoped: true },
        ],
      },
      {
        key: 'site',
        label: 'On Site',
        screens: [{ key: 'my_installations', label: 'My Installations', actions: ['view', 'edit'], special: [{ key: 'supervise', label: 'Act for any crew' }] }],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [{ key: 'installation_reports', label: 'Installation Reports', actions: REPORT, scoped: true }],
      },
    ],
  },
  {
    key: 'service',
    name: 'After-Sales',
    shortName: 'Service',
    description: 'Customer complaints and warranty: service tickets traced to production, repairs, replacements and quality feedback',
    order: 4.6,
    sectionNav: true,
    menus: [
      {
        key: 'tickets',
        label: 'Service',
        screens: [
          {
            key: 'service_tickets',
            label: 'Service Tickets',
            actions: ['view', 'create', 'edit', 'print', 'export'],
            scoped: true,
            special: [
              { key: 'assess', label: 'Assess: warranty decision, root cause and resolution' },
              { key: 'warranty_override', label: 'Override the warranty (goodwill / out of warranty)' },
              { key: 'repair', label: 'Order repairs, visits and replacements' },
              { key: 'close', label: 'Resolve and close tickets' },
            ],
          },
          { key: 'service_board', label: 'Service Board', actions: ['view'], scoped: true, viewImpliedBy: ['service.service_tickets.view'] },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [{ key: 'service_reports', label: 'Service & Quality Reports', actions: REPORT, scoped: true }],
      },
    ],
  },
  {
    key: 'accounting',
    name: 'Accounting',
    shortName: 'Accounts',
    description: 'Ledgers, vouchers, receivables and payables',
    order: 5,
    sectionNav: true,
    menus: [
      {
        key: 'transactions',
        label: 'Transactions',
        replaces: ['vouchers'],
        screens: [
          { key: 'receipts', label: 'Receipts', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'approve_discount', label: 'Approve receipts with a discount' }] },
          { key: 'payments', label: 'Payments', actions: DOC, scoped: true, special: [...DOC_SPECIAL, { key: 'approve_discount', label: 'Approve payments with a discount' }, { key: 'approve_staff_loans', label: "Approve employee loans, and advances above one month's salary" }] },
          // The supplier bill (LPV) and purchase return (LPR) live in Purchasing; Accounts sees and enters them here.
          { key: 'purchase_vouchers', label: 'Purchase Vouchers', actions: ['view'], viewImpliedBy: ['inventory.purchase_bills.view'] },
          { key: 'journal_vouchers', label: 'Journal Vouchers', actions: DOC, scoped: true, special: DOC_SPECIAL },
          // Advances against invoices / bills (owner, 2026-10-28: in the menu again, redone as Adjust Advances).
          { key: 'allocations', label: 'Adjust Advances', actions: ['view', 'create', 'delete'] },
        ],
      },
      {
        key: 'approvals',
        label: 'Approvals & Verification',
        replaces: ['transactions'],
        screens: [
          // One screen for every voucher stage (owner, 2026-10-26). The stage keys below stay as permissions: they decide which cards a role sees.
          { key: 'voucher_manager', label: 'Approvals', actions: ['view', 'export'], viewImpliedBy: ['accounting.journal_vouchers.view', 'accounting.receipts.view', 'accounting.payments.view', 'accounting.salary_vouchers.view'] },
          { key: 'pending_vouchers', hidden: true, hub: 'voucher_manager', label: 'Pending Approval', actions: ['view'], viewImpliedBy: ['accounting.journal_vouchers.view', 'accounting.receipts.view', 'accounting.payments.view', 'accounting.salary_vouchers.view'] },
          {
            key: 'rectification_vouchers',
            hidden: true,
            hub: 'voucher_manager',
            label: 'Sent for Rectification',
            actions: ['view'],
            special: [{ key: 'view_all', label: 'See vouchers other users created (without this, each user sees only their own)' }],
            viewImpliedBy: ['accounting.journal_vouchers.view', 'accounting.receipts.view', 'accounting.payments.view', 'accounting.salary_vouchers.view'],
          },
          { key: 'approved_vouchers', hidden: true, hub: 'voucher_manager', label: 'Approved Vouchers', actions: ['view'], viewImpliedBy: ['accounting.journal_vouchers.view', 'accounting.receipts.view', 'accounting.payments.view', 'accounting.salary_vouchers.view'] },
          { key: 'cancelled_vouchers', hidden: true, hub: 'voucher_manager', label: 'Cancelled Vouchers', actions: ['view'], viewImpliedBy: ['accounting.journal_vouchers.view', 'accounting.receipts.view', 'accounting.payments.view', 'accounting.salary_vouchers.view'] },
          // Opened from Pending Approval for a salary voucher (checked and posted on its own page).
          { key: 'salary_vouchers', hidden: true, hub: 'voucher_manager', label: 'Salary Vouchers', actions: ['view', 'post', 'reject', 'print'] },
        ],
      },
      {
        key: 'banking',
        label: 'Banking',
        screens: [
          {
            key: 'cheques',
            label: 'Cheques (PDC)',
            actions: ['view', 'print', 'export'],
            openedByTabs: true,
            special: [
              { key: 'clear', label: 'Clear cheque' },
              { key: 'return', label: 'Return / bounce cheque' },
              { key: 'deposit', label: 'Mark cheque deposited' },
              { key: 'undo_deposit', label: 'Undo cheque deposit' },
            ],
          },
          // Our cheque books and every leaf: unused, held, issued, cleared, returned, voided (owner, 2026-10-28; was in Settings).
          { key: 'cheque_books', label: 'Cheque Books', actions: MASTER, special: [{ key: 'release', label: 'Release a voided cheque leaf' }] },
          {
            // A view inside Cheques (PDC) (owner, 2026-10-30).
            key: 'recovery_cases',
            hidden: true,
            hub: 'cheques',
            label: 'Bounced Cheques',
            actions: ['view', 'create', 'edit', 'print', 'export'],
            special: [
              { key: 'legal', label: 'Send a case to legal / collection' },
              { key: 'write_off', label: 'Write off what cannot be recovered' },
              { key: 'release_hold', label: 'Approve orders and deliveries for a customer on credit hold' },
            ],
          },
          { key: 'bank_reconciliation', label: 'Bank Reconciliation', actions: ['view', 'create', 'edit', 'approve', 'export'] },
        ],
      },
      {
        key: 'books',
        label: 'Books',
        replaces: ['masters'],
        screens: [
          { key: 'chart_of_accounts', label: 'Chart of Accounts', actions: MASTER },
          // Off the menu (owner, 2026-10-30: not used; assets are bought through bills / journals to asset accounts). Kept for later.
          { key: 'fixed_assets', hidden: true, label: 'Fixed Assets', actions: ['view', 'create', 'edit', 'export'], special: [{ key: 'depreciate', label: 'Run monthly depreciation' }, { key: 'dispose', label: 'Dispose of an asset' }] },
          { key: 'general_ledger', label: 'General Ledgers', actions: ['view', 'print', 'export'], viewImpliedBy: ['accounting.financial_reports.view'] },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [
          {
            key: 'financial_reports',
            label: 'Financial Reports',
            actions: REPORT,
            special: [{ key: 'close_period', label: 'Close / reopen period' }],
            scoped: false,
            openedByTabs: true,
          },
          {
            // Off the menu (owner, 2026-10-28: Financial Reports' Transaction summary covers it); kept for links that open vouchers.
            key: 'all_vouchers',
            hidden: true,
            label: 'Voucher Register',
            actions: ['view', 'print', 'export'],
            viewImpliedBy: ['accounting.journal_vouchers.view', 'accounting.receipts.view', 'accounting.payments.view'],
          },
          // A tab of Financial Reports (owner, 2026-10-30); approvers who see it get that tab only.
          { key: 'approval_report', hidden: true, hub: 'financial_reports', label: 'Approval Turnaround', actions: ['view', 'export'], viewImpliedBy: ['accounting.journal_vouchers.approve', 'accounting.receipts.approve', 'accounting.payments.approve'] },
          { key: 'ledger_reports', label: 'Ledger & Cheque Reports', actions: ['view', 'export', 'print'], scoped: false, viewImpliedBy: ['accounting.financial_reports.view'] },
          // Invoice- / bill-wise outstanding (owner, 2026-10-27), seen by whoever sees the financial reports; listed inside Ledger
          // Reports (owner, 2026-10-28). Supplier Outstanding also plans payments (falling due, Pay); both write off small balances.
          { key: 'customer_outstanding', hidden: true, hub: 'ledger_reports', label: 'Customer Outstanding', actions: ['view', 'print', 'export'], scoped: false, viewImpliedBy: ['accounting.financial_reports.view'] },
          { key: 'salesperson_outstanding', hidden: true, hub: 'ledger_reports', label: 'Salesperson Outstanding', actions: ['view', 'print', 'export'], scoped: false, viewImpliedBy: ['accounting.financial_reports.view'] },
          { key: 'supplier_outstanding', hidden: true, hub: 'ledger_reports', label: 'Supplier Outstanding', actions: ['view', 'print', 'export'], scoped: false, viewImpliedBy: ['accounting.financial_reports.view'] },
          { key: 'management_reports', hidden: true, hub: 'financial_reports', label: 'Management Reports', actions: ['view', 'export', 'print'] },
          { key: 'vat_return', hidden: true, hub: 'financial_reports', label: 'VAT Return', actions: ['view', 'export', 'print'] },
          { key: 'legacy_reconciliation', hidden: true, hub: 'financial_reports', label: 'Legacy Reconciliation', actions: ['view', 'export'] },
        ],
      },
      {
        // Hidden from Accounts for now (owner, 2026-10-23): screens and permissions stay for later.
        key: 'costing',
        label: 'Production Costing',
        screens: [
          { key: 'overhead_rate', hidden: true, label: 'Overhead Rate', actions: ['view', 'edit'] },
          { key: 'cost_variance', hidden: true, label: 'Cost Variance', actions: REPORT },
          { key: 'order_profitability', hidden: true, label: 'Order Profitability', actions: REPORT },
          { key: 'production_close', hidden: true, label: 'Production Month-end', actions: ['view', 'create'] },
        ],
      },
      {
        key: 'period_close',
        label: 'Period Close',
        replaces: ['reports'],
        screens: [
          {
            key: 'period_close',
            label: 'Month-end Close',
            hidden: true,
            hub: 'accounting_settings',
            actions: ['view'],
            special: [
              { key: 'close_period', label: 'Close a month' },
              { key: 'reopen_period', label: 'Reopen a month' },
            ],
            viewImpliedBy: ['accounting.financial_reports.view'],
          },
        ],
      },
      {
        key: 'settings',
        label: 'Configurations',
        replaces: ['masters'],
        screens: [
          {
            key: 'accounting_settings',
            label: 'Settings',
            actions: ['view'],
            viewImpliedBy: ['accounting.posting_accounts.view', 'accounting.voucher_change_settings.view', 'accounting.cost_centres.view', 'accounting.tax_codes.view', 'accounting.payment_terms.view', 'accounting.bank_accounts.view', 'accounting.cheque_books.view', 'accounting.period_close.view'],
          },
          { key: 'posting_accounts', hidden: true, hub: 'accounting_settings', label: 'Posting Accounts', actions: ['view', 'edit'] },
          { key: 'voucher_change_settings', hidden: true, hub: 'accounting_settings', label: 'Voucher Changes', actions: ['view', 'edit'] },
          { key: 'cost_centres', hidden: true, hub: 'accounting_settings', label: 'Cost Centres', actions: MASTER },
          { key: 'tax_codes', hidden: true, hub: 'accounting_settings', label: 'VAT Codes', actions: MASTER },
          { key: 'payment_terms', hidden: true, hub: 'accounting_settings', label: 'Payment Terms', actions: MASTER },
          { key: 'bank_accounts', hidden: true, hub: 'accounting_settings', label: 'Bank Accounts', actions: MASTER },
        ],
      },
    ],
  },
  {
    key: 'hr',
    name: 'HR & Payroll',
    shortName: 'HR',
    description: 'Employees, attendance and payroll',
    order: 6,
    sectionNav: true,
    menus: [
      {
        key: 'people',
        label: 'People',
        screens: [
          { key: 'employees', label: 'Employees', actions: CRUD, scoped: true },
          { key: 'salaries', label: 'Salaries & Allowances', actions: ['view', 'create', 'edit', 'delete', 'export'], scoped: true, special: [{ key: 'approve', label: 'Approve increments and allowance changes' }] },
        ],
      },
      {
        key: 'time',
        label: 'Attendance & Leave',
        screens: [
          {
            key: 'attendance',
            label: 'Attendance',
            actions: ['view', 'create', 'edit', 'delete', 'print', 'export'],
            scoped: true,
            special: [
              { key: 'import', label: 'Import punches' },
              { key: 'approve_corrections', label: 'Approve attendance corrections' },
              { key: 'approve_exempt', label: 'Approve who does not need to punch' },
              { key: 'approve_overtime', label: 'Approve overtime (first level)' },
              { key: 'approve_overtime_final', label: 'Approve overtime (final level, when two levels)' },
            ],
          },
          { key: 'leave_requests', label: 'Leave Requests', actions: DOC, scoped: true, special: DOC_SPECIAL },
          { key: 'leave_balances', label: 'Leave Balances', hidden: true, hub: 'hr_reports', actions: ['view', 'export'], scoped: true, viewImpliedBy: ['hr.leave_requests.view'] },
        ],
      },
      {
        key: 'payroll',
        label: 'Payroll',
        screens: [
          { key: 'payroll_runs', label: 'Payroll Runs', actions: DOC, special: [...DOC_SPECIAL, { key: 'hold_pay', label: "Hold or release an employee's pay" }] },
          { key: 'loans', label: 'Advances & Loans', hidden: true, hub: 'hr_reports', actions: ['view', 'print', 'export'], scoped: true, special: [{ key: 'recovery', label: 'Change recoveries (skip or change a month, reschedule)' }] },
          /** Old address only: HR Reports › Allowances is where the list is now. */
          { key: 'allowances', label: 'Allowances', hidden: true, hub: 'hr_reports', actions: ['view', 'export'], viewImpliedBy: ['hr.salaries.view'] },
          { key: 'payroll_adjustments', label: 'Earnings & Deductions', actions: ['view', 'create', 'edit', 'delete', 'export'], scoped: true },
          { key: 'gratuity', label: 'Gratuity', actions: ['view', 'edit', 'export'], scoped: true },
          { key: 'air_tickets', label: 'Air Tickets', actions: ['view', 'create', 'cancel', 'export'], scoped: true, special: [{ key: 'openings', label: 'Load opening ticket balances' }] },
          { key: 'final_settlements', label: 'Final Settlements', actions: DOC, scoped: true, special: DOC_SPECIAL },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        screens: [
          { key: 'hr_reports', label: 'HR Reports', actions: REPORT, scoped: true },
          { key: 'activity_log', label: 'Activity Log', actions: ['view', 'export'] },
        ],
      },
      {
        key: 'settings',
        label: 'Configurations',
        replaces: ['people', 'time'],
        screens: [
          {
            key: 'hr_settings',
            label: 'Settings',
            actions: ['view'],
            viewImpliedBy: ['hr.designations.view', 'hr.allowance_types.view', 'hr.department_tree.view', 'hr.work_schedules.view', 'hr.shift_periods.view', 'hr.holidays.view', 'hr.leave_types.view', 'hr.policies.view', 'hr.attendance_policies.view', 'hr.attendance_devices.view'],
          },
          { key: 'designations', hidden: true, hub: 'hr_settings', label: 'Designations', actions: MASTER },
          { key: 'allowance_types', hidden: true, hub: 'hr_settings', label: 'Allowance Types', actions: MASTER },
          { key: 'department_tree', hidden: true, hub: 'hr_settings', label: 'Department Hierarchy', actions: ['view', 'edit'] },
          { key: 'work_schedules', hidden: true, hub: 'hr_settings', label: 'Shifts', actions: MASTER },
          { key: 'attendance_policies', hidden: true, hub: 'hr_settings', label: 'Attendance Policies', actions: MASTER },
          { key: 'attendance_devices', hidden: true, hub: 'hr_settings', label: 'Attendance Machines', actions: MASTER, special: [{ key: 'sync', label: 'Read the machine now' }] },
          { key: 'shift_periods', hidden: true, hub: 'hr_settings', label: 'Seasonal Shifts', actions: MASTER },
          { key: 'holidays', hidden: true, hub: 'hr_settings', label: 'Holidays', actions: MASTER },
          { key: 'leave_types', hidden: true, hub: 'hr_settings', label: 'Leave Types', actions: MASTER },
          { key: 'policies', hidden: true, hub: 'hr_settings', label: 'Policies', actions: MASTER },
        ],
      },
    ],
  },
  {
    key: 'admin',
    name: 'Administration',
    shortName: 'Admin',
    description: 'Users, roles, permissions and company setup',
    order: 7,
    sectionNav: true,
    menus: [
      {
        key: 'security',
        label: 'Users & Access',
        screens: [
          {
            key: 'users',
            label: 'Users',
            actions: CRUD,
            special: [
              { key: 'reset_password', label: 'Reset password' },
              { key: 'unlock', label: 'Unlock account' },
              { key: 'revoke_sessions', label: 'Revoke sessions' },
            ],
          },
          { key: 'roles', label: 'Roles & Permissions', actions: CRUD },
        ],
      },
      {
        key: 'monitoring',
        label: 'Monitoring',
        replaces: ['security'],
        screens: [
          { key: 'audit_log', label: 'Audit Log', actions: ['view', 'export'] },
          { key: 'login_history', label: 'Login History', actions: ['view', 'export'] },
        ],
      },
      {
        key: 'organization',
        label: 'Organization',
        screens: [
          { key: 'company', label: 'Company Settings', actions: ['view', 'edit'] },
          { key: 'branches', label: 'Branches', actions: MASTER },
          { key: 'departments', label: 'Departments', actions: MASTER },
        ],
      },
      {
        key: 'platform',
        label: 'Configurations',
        screens: [
          {
            key: 'admin_settings',
            label: 'System Settings',
            actions: ['view'],
            viewImpliedBy: ['admin.numbering.view', 'admin.fiscal_periods.view', 'admin.backdating.view', 'admin.settings.view', 'admin.approval_policies.view'],
          },
          { key: 'settings', hidden: true, hub: 'admin_settings', label: 'General', actions: ['view', 'edit'] },
          { key: 'approval_policies', hidden: true, hub: 'admin_settings', label: 'Workflow & Approvals', actions: ['view', 'edit'] },
          { key: 'numbering', hidden: true, hub: 'admin_settings', label: 'Document Numbering', actions: ['view', 'edit'] },
          {
            key: 'fiscal_periods',
            hidden: true,
            hub: 'admin_settings',
            label: 'Fiscal Years & Periods',
            actions: ['view', 'create'],
            special: [
              { key: 'close_period', label: 'Close period' },
              { key: 'reopen_period', label: 'Reopen period' },
            ],
          },
          {
            key: 'backdating',
            hidden: true,
            hub: 'admin_settings',
            label: 'Back-dating Rules',
            actions: ['view', 'create', 'edit', 'delete'],
          },
        ],
      },
      {
        key: 'golive',
        label: 'Go-live',
        replaces: ['platform'],
        screens: [
          { key: 'cutover', label: 'Go-live Readiness', actions: ['view', 'edit'] },
          { key: 'migration', label: 'Data Migration', actions: ['view'], special: [{ key: 'run', label: 'Run migrations' }] },
        ],
      },
      {
        key: 'developer',
        label: 'Design System',
        screens: [{ key: 'ui_kit', label: 'Form Patterns', actions: ['view'] }],
      },
    ],
  },
];

export const MODULES_BY_KEY: Record<ModuleKey, ModuleDef> = Object.fromEntries(
  MODULES.map((m) => [m.key, m]),
) as Record<ModuleKey, ModuleDef>;

export const moduleAccessKey = (m: string) => `${m}.access`;
export const menuKey = (m: string, menu: string) => `${m}.menu.${menu}`;
export const screenActionKey = (m: string, screen: string, action: string) => `${m}.${screen}.${action}`;

export interface PermissionEntry {
  key: string;
  module: ModuleKey;
  kind: 'module' | 'menu' | 'action';
  menu?: string;
  screen?: string;
  action?: string;
  label: string;
  scoped: boolean;
}

/** Flat list of every permission key in the system. */
export function listPermissions(): PermissionEntry[] {
  const out: PermissionEntry[] = [];
  for (const m of MODULES) {
    out.push({ key: moduleAccessKey(m.key), module: m.key, kind: 'module', label: `Access ${m.name}`, scoped: false });
    for (const menu of m.menus) {
      out.push({
        key: menuKey(m.key, menu.key),
        module: m.key,
        kind: 'menu',
        menu: menu.key,
        label: `${m.name} › ${menu.label}`,
        scoped: false,
      });
      for (const s of menu.screens) {
        const all = [...s.actions.map((a) => ({ key: a, label: ACTION_LABELS[a] })), ...(s.special ?? [])];
        for (const a of all) {
          out.push({
            key: screenActionKey(m.key, s.key, a.key),
            module: m.key,
            kind: 'action',
            menu: menu.key,
            screen: s.key,
            action: a.key,
            label: `${s.label} › ${a.label}`,
            scoped: !!s.scoped,
          });
        }
      }
    }
  }
  return out;
}

export const ALL_PERMISSION_KEYS: ReadonlySet<string> = new Set(listPermissions().map((p) => p.key));

/** Effective permissions as sent to the client: key -> widest data scope. */
export type PermissionMap = Record<string, DataScope>;

/**
 * True when a user may perform `module.screen.action`. A screen action also
 * requires access to its module and menu, so revoking a module or menu hides
 * everything beneath it.
 */
export function canDo(perms: PermissionMap, module: string, screen: string, action: string): boolean {
  const mod = MODULES_BY_KEY[module as ModuleKey];
  if (!mod || !perms[moduleAccessKey(module)]) return false;
  const menu = mod.menus.find((mn) => mn.screens.some((s) => s.key === screen));
  if (!menu || !perms[menuKey(module, menu.key)]) return false;
  return !!perms[screenActionKey(module, screen, action)];
}

/** May open the screen: view on it, or (a hub opened by its tabs) view on one of its tabs. */
export function canOpenScreen(perms: PermissionMap, module: string, screen: string): boolean {
  if (canDo(perms, module, screen, 'view')) return true;
  const screens = MODULES_BY_KEY[module as ModuleKey]?.menus.flatMap((m) => m.screens) ?? [];
  return !!screens.find((s) => s.key === screen)?.openedByTabs && screens.some((s) => s.hub === screen && canDo(perms, module, s.key, 'view'));
}

export function canAccessModule(perms: PermissionMap, module: string): boolean {
  return !!perms[moduleAccessKey(module)];
}

/** Sidebar names for screens whose full label does not fit (≤ 18 characters). */
const NAV_LABELS: Record<string, string> = {
  'sales.release_gate': 'Release Gate',
  'inventory.held_stock': 'Held for Orders',
  'inventory.purchase_requisitions': 'Requisitions',
  'inventory.special_requests': 'Special Request Notes',
  'inventory.production_returns': 'Return from Production',
  'inventory.out_gate_passes': 'Outward Gate Passes',
  'inventory.shelves': 'Shelves',
  'inventory.approvals': 'Approvals',
  'inventory.planning_check': 'Planning Check',
  'inventory.gate_passes': 'Inward Gate Passes',
  'inventory.goods_receipts': 'Goods Receipts',
  'inventory.products': 'Products',
  'inventory.store_reports': 'Store Reports',
  'manufacturing.drawings': 'Drawings & Dates',
  'manufacturing.material_requisitions': 'Material Requests',
  'manufacturing.fg_receipts': 'FG Receipts',
  'installation.installation_orders': 'Installations',
  'installation.installation_reports': 'Install Reports',
  'service.service_reports': 'Service Reports',
  'accounting.bank_reconciliation': 'Bank Reconciling',
  'accounting.order_profitability': 'Profitability',
  'accounting.production_close': 'WIP Month-end',
  'hr.payroll_adjustments': 'Pay Adjustments',
  'admin.roles': 'Roles',
  'accounting.ledger_reports': 'Ledger Reports',
};
for (const m of MODULES) for (const menu of m.menus) for (const sc of menu.screens) {
  const short = NAV_LABELS[`${m.key}.${sc.key}`];
  if (short) (sc as { navLabel?: string }).navLabel = short;
}
